/**
 * Government Contracting Tool Configurations
 *
 * Tools for RFP analysis, bid scoring, SAM.gov, LCAT, PMP generation, etc.
 */ import { defineToolConfig, formatCount, hasProperty } from '../tool-display-config';
// ============================================================================
// Progress Steps
// ============================================================================
const lcatProgressSteps = [
    {
        id: 'initialization',
        label: 'Initializing',
        loadingText: 'Starting analysis...'
    },
    {
        id: 'fetch-attachments',
        label: 'Fetching Files',
        loadingText: 'Fetching attachments...'
    },
    {
        id: 'detect-schedule-b',
        label: 'Detect Schedule B',
        loadingText: 'Detecting Schedule B...'
    },
    {
        id: 'identify-pws',
        label: 'Identify PWS',
        loadingText: 'Identifying main PWS...'
    },
    {
        id: 'parse-pws',
        label: 'Parse PWS',
        loadingText: 'Parsing PWS sections...'
    },
    {
        id: 'extract-clins',
        label: 'Extract CLINs',
        loadingText: 'Extracting CLINs...'
    },
    {
        id: 'extract-roles',
        label: 'Extract Roles',
        loadingText: 'Extracting functional roles...'
    },
    {
        id: 'map-attachments',
        label: 'Map Attachments',
        loadingText: 'Mapping attachments...'
    },
    {
        id: 'compliance',
        label: 'Compliance Matrix',
        loadingText: 'Building compliance matrix...'
    },
    {
        id: 'export',
        label: 'Export',
        loadingText: 'Exporting results...'
    }
];
const contractAnalysisProgressSteps = [
    {
        id: 'reading',
        label: 'Reading Document',
        loadingText: 'Reading contract document...'
    },
    {
        id: 'converting',
        label: 'Converting',
        loadingText: 'Converting to markdown...'
    },
    {
        id: 'header',
        label: 'Extract Header',
        loadingText: 'Extracting header info...'
    },
    {
        id: 'parties',
        label: 'Extract Parties',
        loadingText: 'Extracting parties...'
    },
    {
        id: 'period',
        label: 'Extract Period',
        loadingText: 'Extracting period of performance...'
    },
    {
        id: 'clin-tables',
        label: 'Detect CLINs',
        loadingText: 'Detecting CLIN tables...'
    },
    {
        id: 'parse-rows',
        label: 'Parse Rows',
        loadingText: 'Parsing table rows...'
    },
    {
        id: 'enrich',
        label: 'Enrich CLINs',
        loadingText: 'Enriching CLINs with AI...'
    },
    {
        id: 'group',
        label: 'Group by Period',
        loadingText: 'Grouping CLINs by period...'
    },
    {
        id: 'save',
        label: 'Save',
        loadingText: 'Saving analysis...'
    }
];
// ============================================================================
// Tool Configurations
// ============================================================================
const bidNoBidConfig = defineToolConfig({
    id: 'bid-no-bid-scorer',
    aliases: [
        'bidNoBidScorer',
        'bidNoBidScorerTool'
    ],
    formatSuccess: (output)=>{
        const rec = output.recommendation;
        const score = Math.round(output.overallScore);
        const emoji = rec === 'BID' ? '✅' : rec === 'NO_BID' ? '❌' : '⚠️';
        return `${emoji} ${rec.replace('_', ' ')} (Score: ${score}/100)`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Failed to score opportunity: ${error.message}`;
        }
        return 'Failed to evaluate bid/no-bid';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'recommendation' in output && 'overallScore' in output;
    }
});
const samOpportunitySearchConfig = defineToolConfig({
    id: 'sam-opportunity-search',
    aliases: [
        'samOpportunitySearch',
        'samOpportunitySearchTool'
    ],
    formatSuccess: (output)=>{
        const count = output.opportunities?.length ?? 0;
        const total = output.totalCount ?? count;
        if (count === 0) return 'No opportunities found';
        if (output.hasMore) {
            return `Found ${formatCount(count, 'opportunity', 'opportunities')} (${total} total)`;
        }
        return `Found ${formatCount(count, 'opportunity', 'opportunities')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'code') && error.code === 'RATE_LIMITED') {
            return 'SAM.gov rate limit exceeded. Try again in a few minutes.';
        }
        if (hasProperty(error, 'message')) {
            return `SAM.gov search failed: ${error.message}`;
        }
        return 'Failed to search SAM.gov';
    },
    formatInput: (input)=>{
        if (!input) return [];
        const fields = [];
        if (input.keywords) fields.push({
            label: 'Keywords',
            value: input.keywords
        });
        if (input.naicsCode) fields.push({
            label: 'NAICS',
            value: input.naicsCode
        });
        if (input.setAside) fields.push({
            label: 'Set-aside',
            value: input.setAside
        });
        if (input.agency) fields.push({
            label: 'Agency',
            value: input.agency
        });
        return fields.length > 0 ? fields : [
            {
                label: 'Search',
                value: 'All opportunities'
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'opportunities' in output && Array.isArray(output.opportunities);
    },
    isEmptyResult: (output)=>!output.opportunities?.length
});
const analyzeLcatConfig = defineToolConfig({
    id: 'govcon__analyze-lcat',
    aliases: [
        'analyze-lcat',
        'analyzeLcat',
        'analyzeLCATTool'
    ],
    progressSteps: lcatProgressSteps,
    formatSuccess: (output)=>{
        if (!output.success || !output.summary) {
            return 'LCAT analysis completed';
        }
        const { summary } = output;
        return `Extracted ${formatCount(summary.totalCLINs, 'CLIN')}, ${formatCount(summary.totalRoles, 'role')}, and ${formatCount(summary.totalRequirements, 'requirement')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `LCAT analysis failed: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `LCAT analysis failed: ${error.message}`;
        }
        return 'Failed to analyze PWS for labor categories';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>!output.success
});
const contractAnalysisV2Config = defineToolConfig({
    id: 'govcon__analyze-contract-v2',
    aliases: [
        'contract-analysis-v2',
        'contractAnalysisV2',
        'analyze-contract-v2'
    ],
    progressSteps: contractAnalysisProgressSteps,
    formatSuccess: (output)=>{
        if (!output.success || !output.summary) {
            return 'Contract analysis completed';
        }
        const { summary, contractNumber } = output;
        const contract = contractNumber || 'contract';
        return `Analyzed ${contract}: ${formatCount(summary.totalCLINs, 'CLIN')}, ${formatCount(summary.totalDeliverables, 'deliverable')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `Contract analysis failed: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `Contract analysis failed: ${error.message}`;
        }
        return 'Failed to analyze contract document';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>!output.success
});
const farComplianceConfig = defineToolConfig({
    id: 'far-compliance-check',
    aliases: [
        'farComplianceCheck',
        'farComplianceCheckTool'
    ],
    formatSuccess: (output)=>{
        const issueCount = output.issues?.length ?? 0;
        if (output.compliant) {
            return 'All FAR/DFARS compliance requirements met';
        }
        return `Found ${formatCount(issueCount, 'compliance issue')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Compliance check failed: ${error.message}`;
        }
        return 'Failed to check FAR/DFARS compliance';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'compliant' in output;
    }
});
const samScraperProgressSteps = [
    {
        id: 'fetch',
        label: 'Fetching',
        loadingText: 'Fetching from SAM.gov...'
    },
    {
        id: 'persist',
        label: 'Persisting',
        loadingText: 'Saving to database...'
    },
    {
        id: 'embed',
        label: 'Embedding',
        loadingText: 'Generating embeddings...'
    },
    {
        id: 'match',
        label: 'Matching',
        loadingText: 'Running profile matching...'
    }
];
const samOpportunityScraperConfig = defineToolConfig({
    id: 'sam-opportunity-scraper',
    aliases: [
        'samOpportunityScraper',
        'samOpportunityScraperTool'
    ],
    progressSteps: samScraperProgressSteps,
    formatSuccess: (output)=>{
        if (output.status === 'failed') {
            return `Scraping failed: ${output.errors[0]?.message || 'Unknown error'}`;
        }
        const parts = [];
        if (output.newOpportunities > 0) {
            parts.push(`${output.newOpportunities} new`);
        }
        if (output.updatedOpportunities > 0) {
            parts.push(`${output.updatedOpportunities} updated`);
        }
        if (output.opportunitiesEmbedded > 0) {
            parts.push(`${output.opportunitiesEmbedded} embedded`);
        }
        if (parts.length === 0) {
            return `Scraped ${output.totalFetched} opportunities (no changes)`;
        }
        const duration = (output.duration / 1000).toFixed(1);
        return `Processed ${output.totalFetched} opportunities: ${parts.join(', ')} (${duration}s)`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `SAM scraper failed: ${error.message}`;
        }
        return 'Failed to scrape SAM.gov opportunities';
    },
    formatInput: (input)=>{
        if (!input) return [];
        const fields = [];
        if (input.postedFrom || input.postedTo) {
            fields.push({
                label: 'Date Range',
                value: `${input.postedFrom || 'Any'} to ${input.postedTo || 'Today'}`
            });
        }
        if (input.maxRecords) {
            fields.push({
                label: 'Max Records',
                value: String(input.maxRecords)
            });
        }
        return fields.length > 0 ? fields : [
            {
                label: 'Mode',
                value: 'Default (2-day lookback)'
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'status' in output && 'totalFetched' in output;
    },
    isErrorResult: (output)=>output.status === 'failed'
});
const samAwardsQueryConfig = defineToolConfig({
    id: 'sam-awards-query',
    aliases: [
        'samAwardsQuery',
        'samAwardsQueryTool'
    ],
    formatSuccess: (output)=>{
        if (output.success === false) {
            return output.message || 'SAM.gov awards search failed';
        }
        const count = output.returned ?? 0;
        const total = output.totalRecords ?? count;
        const obligated = output.summary?.actionObligationSum;
        const dollars = typeof obligated === 'number' ? ` · $${obligated.toLocaleString(undefined, {
            maximumFractionDigits: 0
        })} obligated` : '';
        if (count === 0) return 'No contract awards found';
        const more = output.truncated ? ` of ${total}` : '';
        const found = `Found ${formatCount(count, 'award action', 'award actions')}${more}${dollars}`;
        if (output.documentId) {
            return `${found} · spreadsheet ready`;
        }
        return found;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `SAM.gov awards search failed: ${error.message}`;
        }
        return 'Failed to search SAM.gov contract awards';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>output.success === false
});
const samTrackVendorFootprintConfig = defineToolConfig({
    id: 'sam-track-vendor-footprint',
    aliases: [
        'samTrackVendorFootprint'
    ],
    formatSuccess: (output)=>{
        if (output.success === false) {
            return output.message || 'Failed to build vendor footprint spreadsheet';
        }
        const count = output.totalContracts ?? 0;
        if (count === 0) return 'No contract awards found for this vendor';
        if (output.documentId) {
            return `Opened spreadsheet with ${formatCount(count, 'award action', 'award actions')}`;
        }
        return output.message || `Found ${formatCount(count, 'award action', 'award actions')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Failed to build vendor footprint spreadsheet: ${error.message}`;
        }
        return 'Failed to build vendor footprint spreadsheet';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>output.success === false
});
const samFindIncumbentsConfig = defineToolConfig({
    id: 'sam-find-incumbents',
    aliases: [
        'samFindIncumbents'
    ],
    formatSuccess: (output)=>{
        if (output.success === false) {
            return output.message || 'Failed to find incumbents';
        }
        const count = output.totalContracts ?? 0;
        if (count === 0) return 'No incumbents found in this market';
        if (output.documentId) {
            return `Opened spreadsheet with ${formatCount(count, 'award action', 'award actions')}`;
        }
        return output.message || `Found ${formatCount(count, 'award action', 'award actions')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Failed to find incumbents: ${error.message}`;
        }
        return 'Failed to find incumbents';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>output.success === false
});
const samGetContractRundownConfig = defineToolConfig({
    id: 'sam-get-contract-rundown',
    aliases: [
        'samGetContractRundown'
    ],
    formatSuccess: (output)=>{
        if (output.success === false) {
            return output.message || 'Failed to build contract rundown';
        }
        const count = output.totalContracts ?? 0;
        if (count === 0) return 'No award actions found for this PIID';
        if (output.documentId) {
            return `Opened spreadsheet with ${formatCount(count, 'award action', 'award actions')}`;
        }
        return output.message || `Found ${formatCount(count, 'award action', 'award actions')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Failed to build contract rundown: ${error.message}`;
        }
        return 'Failed to build contract rundown';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>output.success === false
});
const samRecompeteForecastingConfig = defineToolConfig({
    id: 'sam-recompete-forecasting',
    aliases: [
        'samRecompeteForecasting'
    ],
    formatSuccess: (output)=>{
        if (output.success === false) {
            return output.message || 'Failed to forecast recompetes';
        }
        const count = output.totalContracts ?? 0;
        if (count === 0) return 'No expiring awards found in this window';
        if (output.documentId) {
            return `Opened spreadsheet with ${formatCount(count, 'award action', 'award actions')}`;
        }
        return output.message || `Found ${formatCount(count, 'award action', 'award actions')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Failed to forecast recompetes: ${error.message}`;
        }
        return 'Failed to forecast recompetes';
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>output.success === false
});
// ============================================================================
// Export
// ============================================================================
export const govconToolConfigs = [
    bidNoBidConfig,
    samAwardsQueryConfig,
    samTrackVendorFootprintConfig,
    samFindIncumbentsConfig,
    samGetContractRundownConfig,
    samRecompeteForecastingConfig,
    samOpportunitySearchConfig,
    samOpportunityScraperConfig,
    analyzeLcatConfig,
    contractAnalysisV2Config,
    farComplianceConfig
];
